import React, { useState } from 'react';
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react';

const FeatureComparison = ({ plans }) => {
  const [expanded, setExpanded] = useState(false);

  const comparisonFeatures = [
    {
      category: 'Limits & Capacity',
      features: [
        { name: 'Monthly Spending Limit', free: '$1,000', pro: '$10,000', enterprise: 'Unlimited' },
        { name: 'Monthly Swaps', free: '10', pro: '100', enterprise: 'Unlimited' },
        { name: 'API Rate Limit', free: '100/hour', pro: '1,000/hour', enterprise: 'Custom' },
        { name: 'Concurrent Transactions', free: '5', pro: '50', enterprise: 'Unlimited' }
      ]
    },
    {
      category: 'Features',
      features: [
        { name: 'Wallet Connectivity', free: true, pro: true, enterprise: true },
        { name: 'Basic Analytics', free: true, pro: true, enterprise: true },
        { name: 'Advanced Analytics', free: false, pro: true, enterprise: true },
        { name: 'Pro Payout Management', free: false, pro: true, enterprise: true },
        { name: 'API Access', free: false, pro: true, enterprise: true },
        { name: 'Custom Integrations', free: false, pro: false, enterprise: true },
        { name: 'White-label Solutions', free: false, pro: false, enterprise: true }
      ]
    },
    {
      category: 'Fees & Pricing',
      features: [
        { name: 'Transaction Fee', free: '2.9% + $0.30', pro: '2.4% + $0.30', enterprise: 'Custom' },
        { name: 'Swap Fee', free: '1.5%', pro: '1.0%', enterprise: 'Custom' },
        { name: 'Withdrawal Fee', free: '$2.00', pro: '$1.00', enterprise: 'Negotiable' },
        { name: 'International Fee', free: '+1.5%', pro: '+1.0%', enterprise: 'Custom' }
      ]
    },
    {
      category: 'Support',
      features: [
        { name: 'Email Support', free: true, pro: true, enterprise: true },
        { name: 'Chat Support', free: false, pro: true, enterprise: true },
        { name: 'Priority Support', free: false, pro: true, enterprise: true },
        { name: 'Phone Support', free: false, pro: false, enterprise: true },
        { name: 'Dedicated Account Manager', free: false, pro: false, enterprise: true },
        { name: 'Response Time SLA', free: '48 hours', pro: '12 hours', enterprise: '1 hour' }
      ]
    }
  ];

  const renderValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="w-5 h-5 text-green-500 mx-auto" />
      ) : (
        <X className="w-5 h-5 text-gray-300 mx-auto" />
      );
    }
    return <span className="text-sm text-gray-700">{value}</span>;
  };

  const displayedCategories = expanded ? comparisonFeatures : comparisonFeatures?.slice(0, 2);

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 mb-16">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Detailed Feature Comparison</h2>
        <p className="text-gray-600">Compare all features across our plans</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-4 px-4 font-semibold text-gray-900">Feature</th>
              <th className="text-center py-4 px-4 font-semibold text-gray-900">Free</th>
              <th className="text-center py-4 px-4 font-semibold text-gray-900 bg-blue-50 rounded-t-lg">Pro</th>
              <th className="text-center py-4 px-4 font-semibold text-gray-900">Enterprise</th>
            </tr>
          </thead>
          <tbody>
            {displayedCategories?.map((category, categoryIndex) => (
              <React.Fragment key={categoryIndex}>
                <tr className="bg-gray-50">
                  <td colSpan="4" className="py-3 px-4 font-semibold text-gray-900 text-sm uppercase tracking-wide">
                    {category?.category}
                  </td>
                </tr>
                {category?.features?.map((feature, featureIndex) => (
                  <tr key={featureIndex} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4 text-sm text-gray-700">{feature?.name}</td>
                    <td className="py-4 px-4 text-center">{renderValue(feature?.free)}</td>
                    <td className="py-4 px-4 text-center bg-blue-50">{renderValue(feature?.pro)}</td>
                    <td className="py-4 px-4 text-center">{renderValue(feature?.enterprise)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-center mt-6">
        <button
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium text-gray-700 transition-colors"
        >
          {expanded ? (
            <>
              Show Less <ChevronUp className="w-5 h-5" />
            </>
          ) : (
            <>
              Show All Features <ChevronDown className="w-5 h-5" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default FeatureComparison;